// src/pages/admin/SingleOrder.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersAPI, productsAPI } from '../../services/api';
import { 
  ArrowLeft, 
  Package, 
  User, 
  Phone, 
  MapPin, 
  Clock, 
  Loader, 
  AlertCircle, 
  Coins, 
  ShoppingBag, 
  Truck,
  Mail,
  Tag,
  CheckCircle,
  Calendar,
  Settings,
  FileText,
  CreditCard,
  X
} from 'lucide-react';
import './css/SingleOrder.css';

// Safe option label formatter
const formatOptionLabel = (group, value) => {
  const extraPrice = Number(value.extra_price) || 0;
  const extra = extraPrice > 0 ? ` (+DT ${extraPrice.toFixed(2)})` : '';
  return `${group.name}: ${value.value}${extra}`;
};

const SingleOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await ordersAPI.getById(id);
        setOrder(data);

        // Enrich items with option prices
        const itemsWithOptions = data.items.filter(item => {
          let parsedOptions = {};
          try {
            parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
          } catch {
            parsedOptions = {};
          }
          return parsedOptions && Object.keys(parsedOptions).length > 0;
        });

        let enriched = [...data.items];

        if (itemsWithOptions.length > 0) {
          const uniqueProductIds = [...new Set(itemsWithOptions.map(item => item.productId))];
          const productResponses = await Promise.allSettled(
            uniqueProductIds.map(pid => productsAPI.getById(pid))
          );

          const productsMap = {};
          productResponses.forEach((resp, idx) => {
            if (resp.status === 'fulfilled') {
              productsMap[uniqueProductIds[idx]] = resp.value.data;
            }
          });

          enriched = data.items.map(item => {
            let parsedOptions = {};
            try {
              parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
            } catch {
              parsedOptions = {};
            }

            const fullProduct = productsMap[item.productId];
            if (!fullProduct || Object.keys(parsedOptions).length === 0) {
              return {
                ...item,
                finalPrice: Number(item.price || 0),
                optionLabels: null
              };
            }

            const basePrice = Number(fullProduct.salePrice || fullProduct.regularPrice || 0);
            let optionExtra = 0;
            const labels = [];

            fullProduct.options.forEach(group => {
              const selectedValueId = parsedOptions[group.id];
              if (selectedValueId) {
                const value = group.values.find(v => v.id === Number(selectedValueId));
                if (value) {
                  const extra = Number(value.extra_price) || 0;
                  optionExtra += extra;
                  labels.push(formatOptionLabel(group, value));
                }
              }
            });

            const finalPrice = basePrice + optionExtra;

            return {
              ...item,
              finalPrice,
              optionLabels: labels.length > 0 ? labels : null,
              name: fullProduct.name || item.name
            };
          });
        } else {
          enriched = enriched.map(item => ({
            ...item,
            finalPrice: Number(item.price || 0),
            optionLabels: null
          }));
        }

        setEnrichedItems(enriched);
        setLoading(false);

        // Mark notification as viewed
        if (data.notificationStatus === 'unviewed') {
          await ordersAPI.markNotificationViewed(id);
        }
      } catch (err) {
        setError('Impossible de charger les details de la commande');
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await ordersAPI.updateStatus(id, newStatus);
      setOrder(prev => ({ ...prev, status: newStatus }));
      setSuccess('Statut de la commande mis a jour !');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Echec de mise a jour du statut de la commande');
      setTimeout(() => setError(''), 3000);
    }
  };

  const computeTotal = (items, discount, deliveryFee) => {
    const subtotal = items.reduce((sum, item) => sum + (item.finalPrice * item.quantity), 0);
    return Math.max(0, subtotal - (Number(discount) || 0) + (Number(deliveryFee) || 0));
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'so-pending';
      case 'processing': return 'so-processing';
      case 'shipped': return 'so-shipped';
      case 'delivered': return 'so-delivered';
      default: return 'so-default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return <Clock size={16} />;
      case 'processing': return <Package size={16} />;
      case 'shipped': return <Truck size={16} />;
      case 'delivered': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'En attente';
      case 'processing': return 'En cours';
      case 'shipped': return 'Expedie';
      case 'delivered': return 'Livree';
      default: return status;
    }
  };

  const getImageUrl = (imageUrl) => {
    return imageUrl && !imageUrl.startsWith('http') ? `/images/${imageUrl}` : imageUrl || '/placeholder-image.jpg';
  };

  if (loading) {
    return (
      <div className="so-loading-container">
        <div className="so-loading-spinner-wrapper">
          <Loader className="so-loading-spinner" size={48} />
        </div>
        <p className="so-loading-text">Chargement des details de la commande...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="so-error-container">
        <div className="so-error-icon-wrapper">
          <AlertCircle size={48} className="so-error-icon" />
        </div>
        <h2 className="so-error-title">Impossible de charger la commande</h2>
        <p className="so-error-text">{error || 'Commande introuvable'}</p>
        <button onClick={() => navigate('/admin/orders')} className="so-back-button">
          <ArrowLeft size={18} />
          <span>Retour aux commandes</span>
        </button>
      </div>
    );
  }

  const { firstName, lastName, phoneNumber, shippingAddress, email, createdAt, status } = order;
  const subtotal = enrichedItems.reduce((sum, item) => sum + (item.finalPrice * item.quantity), 0);
  const discount = Number(order.discount_amount || 0);
  const deliveryFee = Number(order.delivery_fee || 0);
  const total = computeTotal(enrichedItems, discount, deliveryFee);

  return (
    <div className="so-container">
      {/* Header */}
      <div className="so-header">
        <div className="so-header-content">
          <button onClick={() => navigate('/admin/orders')} className="so-back-button">
            <ArrowLeft size={20} />
            <span>Retour aux commandes</span>
          </button>
          <div className="so-header-info">
            <div className="so-header-icon-wrapper">
              <FileText size={28} />
            </div>
            <div className="so-header-text">
              <h1 className="so-header-title">Commande #{id}</h1>
              <p className="so-header-subtitle">
                Passee le {new Date(createdAt).toLocaleDateString('fr-FR', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
          <div className={`so-status-badge ${getStatusColor(status)}`}>
            {getStatusIcon(status)}
            <span>{getStatusLabel(status)}</span>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(success || error) && (
        <div className="so-alerts">
          {success && (
            <div className="so-alert so-alert-success">
              <CheckCircle size={20} className="so-alert-icon" />
              <span>{success}</span>
              <button className="so-alert-close" onClick={() => setSuccess('')}>
                <X size={16} />
              </button>
            </div>
          )}
          {error && (
            <div className="so-alert so-alert-error">
              <AlertCircle size={20} className="so-alert-icon" />
              <span>{error}</span>
              <button className="so-alert-close" onClick={() => setError('')}>
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="so-content">
        <div className="so-grid">
          {/* Left Column */}
          <div className="so-left-column">
            {/* Customer Info */}
            <div className="so-card">
              <div className="so-card-header">
                <User size={20} />
                <h2 className="so-card-title">Informations client</h2>
              </div>
              <div className="so-card-body">
                <div className="so-info-row">
                  <User size={18} className="so-info-icon" />
                  <div className="so-info-content">
                    <span className="so-info-label">Nom complet</span>
                    <span className="so-info-value">{firstName} {lastName}</span>
                  </div>
                </div>
                <div className="so-info-row">
                  <Mail size={18} className="so-info-icon" />
                  <div className="so-info-content">
                    <span className="so-info-label">Adresse email</span>
                    <span className="so-info-value">{email || 'Non renseigne'}</span>
                  </div>
                </div>
                <div className="so-info-row">
                  <Phone size={18} className="so-info-icon" />
                  <div className="so-info-content">
                    <span className="so-info-label">Telephone</span>
                    <span className="so-info-value">{phoneNumber}</span>
                  </div>
                </div>
                <div className="so-info-row">
                  <MapPin size={18} className="so-info-icon" />
                  <div className="so-info-content">
                    <span className="so-info-label">Adresse de livraison</span>
                    <span className="so-info-value">{shippingAddress}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Timeline */}
            <div className="so-card">
              <div className="so-card-header">
                <Clock size={20} />
                <h2 className="so-card-title">Chronologie de commande</h2>
              </div>
              <div className="so-card-body">
                <div className="so-timeline">
                  <div className={`so-timeline-item ${['pending', 'processing', 'shipped', 'delivered'].includes(status.toLowerCase()) ? 'so-completed' : ''}`}>
                    <div className="so-timeline-dot so-completed"></div>
                    <div className="so-timeline-content">
                      <span className="so-timeline-label">Commande passee</span>
                      <span className="so-timeline-date">
                        {new Date(createdAt).toLocaleString('fr-FR', { 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                  <div className={`so-timeline-item ${['processing', 'shipped', 'delivered'].includes(status.toLowerCase()) ? 'so-completed' : 'so-inactive'}`}>
                    <div className={`so-timeline-dot ${['processing', 'shipped', 'delivered'].includes(status.toLowerCase()) ? 'so-completed' : ''}`}></div>
                    <div className="so-timeline-content">
                      <span className="so-timeline-label">En cours</span>
                      <span className="so-timeline-date">
                        {['processing', 'shipped', 'delivered'].includes(status.toLowerCase()) ? 'En cours' : 'En attente'}
                      </span>
                    </div>
                  </div>
                  <div className={`so-timeline-item ${['shipped', 'delivered'].includes(status.toLowerCase()) ? 'so-completed' : 'so-inactive'}`}>
                    <div className={`so-timeline-dot ${['shipped', 'delivered'].includes(status.toLowerCase()) ? 'so-completed' : ''}`}></div>
                    <div className="so-timeline-content">
                      <span className="so-timeline-label">Expedie</span>
                      <span className="so-timeline-date">
                        {['shipped', 'delivered'].includes(status.toLowerCase()) ? 'En route' : 'En attente'}
                      </span>
                    </div>
                  </div>
                  <div className={`so-timeline-item ${status.toLowerCase() === 'delivered' ? 'so-completed' : 'so-inactive'}`}>
                    <div className={`so-timeline-dot ${status.toLowerCase() === 'delivered' ? 'so-completed' : ''}`}></div>
                    <div className="so-timeline-content">
                      <span className="so-timeline-label">Livree</span>
                      <span className="so-timeline-date">
                        {status.toLowerCase() === 'delivered' ? 'Terminee' : 'En attente'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Update */}
            <div className="so-card">
              <div className="so-card-header">
                <Settings size={20} />
                <h2 className="so-card-title">Mettre a jour le statut</h2>
              </div>
              <div className="so-card-body">
                <div className="so-status-update">
                  <label className="so-status-label">Statut actuel</label>
                  <select 
                    value={status} 
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className={`so-status-select ${getStatusColor(status)}`}
                  >
                    <option value="pending">En attente</option>
                    <option value="processing">En cours</option>
                    <option value="shipped">Expedie</option>
                    <option value="delivered">Livree</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="so-right-column">
            {/* Order Items */}
            <div className="so-card">
              <div className="so-card-header">
                <ShoppingBag size={20} />
                <h2 className="so-card-title">Articles de la commande</h2>
                <span className="so-items-count">{enrichedItems.length}</span>
              </div>
              <div className="so-card-body">
                <div className="so-items-list">
                  {enrichedItems.map((item, index) => {
                    const itemTotal = item.finalPrice * item.quantity;

                    return (
                      <div key={index} className="so-item">
                        <div className="so-item-image">
                          <img 
                            src={getImageUrl(item.imageUrl)} 
                            alt={item.name}
                            onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                          />
                        </div>
                        <div className="so-item-details">
                          <h4 className="so-item-name">{item.name}</h4>
                          {item.optionLabels && (
                            <div className="so-item-options">
                              <Settings size={12} />
                              <span>{item.optionLabels.join(', ')}</span>
                            </div>
                          )}
                          <div className="so-item-meta">
                            <span className="so-item-price">
                              <Coins size={13} />
                              DT {item.finalPrice.toFixed(2)} / unite
                            </span>
                            <span className="so-item-separator">-</span>
                            <span className="so-item-quantity">Qte : {item.quantity}</span>
                          </div>
                        </div>
                        <div className="so-item-total">
                          <Coins size={16} />
                          <span>DT {itemTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="so-card">
              <div className="so-card-header">
                <CreditCard size={20} />
                <h2 className="so-card-title">Resume du paiement</h2>
              </div>
              <div className="so-card-body">
                <div className="so-payment-summary">
                  <div className="so-summary-row">
                    <span className="so-summary-label">Sous-total</span>
                    <span className="so-summary-value">DT {subtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="so-summary-row so-discount-row">
                      <span className="so-summary-label">
                        <Tag size={14} />
                        Remise {order.promo_code && <span className="so-promo-code">{order.promo_code}</span>}
                      </span>
                      <span className="so-summary-value so-discount-value">-DT {discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="so-summary-row">
                    <span className="so-summary-label">
                      <Truck size={14} />
                      Frais de livraison
                    </span>
                    <span className="so-summary-value">
                      {deliveryFee === 0 ? 'Gratuit' : `DT ${deliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="so-summary-divider"></div>
                  <div className="so-summary-total">
                    <span className="so-total-label">Montant total</span>
                    <div className="so-total-value">
                      <Coins size={20} />
                      <span>DT {total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleOrder;
